import { useCallback, useEffect, useRef, useState } from 'react';

// useAutosave — the 5C recipe from `BBM Context for claude/feedback.md`.
//
//   const auto = useAutosave({ save: async () => {...}, delay: 1200, enabled });
//   auto.touch();            // call after every field change (debounces a save)
//   await auto.flush();      // save now if anything is pending (close / switch)
//   auto.status              // 'idle' | 'dirty' | 'saving' | 'saved' | 'error'
//   auto.savedAt, auto.error
//
// `save` returns true on success, or a short error string / false when the
// record can't be saved yet (validation) — the status goes red and the
// pending edit stays pending until the next change. One in-flight save at
// a time; a change during a save re-queues one more save after it.
export function useAutosave({ save, delay = 1200, enabled = true }) {
    const [status, setStatus] = useState('idle');
    const [savedAt, setSavedAt] = useState(null);
    const [error, setError] = useState(null);
    const timerRef = useRef(null);
    const inflightRef = useRef(false);
    const pendingRef = useRef(false);
    const saveRef = useRef(save);
    const errorRef = useRef(null);
    useEffect(() => { saveRef.current = save; }, [save]);

    const run = useCallback(async () => {
        if (inflightRef.current) { pendingRef.current = true; return; }
        inflightRef.current = true;
        pendingRef.current = false;
        setStatus('saving');
        let result;
        try { result = await saveRef.current(); }
        catch (e) { result = (e && e.message) || 'save failed'; }
        inflightRef.current = false;
        if (result === true) {
            errorRef.current = null; setError(null); setSavedAt(Date.now()); setStatus('saved');
        } else {
            errorRef.current = typeof result === 'string' ? result : 'not saved'; setError(errorRef.current); setStatus('error');
        }
        if (pendingRef.current) run();
    }, []);

    const touch = useCallback(() => {
        if (!enabled) return;
        setStatus((s) => (s === 'saving' ? s : 'dirty'));
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => { timerRef.current = null; run(); }, delay);
    }, [enabled, delay, run]);

    // Save immediately if a debounce is pending or a save is in flight.
    // Resolves true when nothing is left unsaved.
    const flush = useCallback(async () => {
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; await run(); }
        // Wait out an in-flight save (and any re-queued one).
        while (inflightRef.current || pendingRef.current) { await new Promise((r) => setTimeout(r, 60)); }
        return !errorRef.current;
    }, [run]);

    useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

    const isPending = status === 'dirty' || status === 'saving' || !!timerRef.current;
    // Live check (reads refs, not render state) — for close guards.
    const pending = () => !!timerRef.current || inflightRef.current || pendingRef.current || !!errorRef.current;
    return { status, savedAt, error, touch, flush, isPending, pending, reset: () => { errorRef.current = null; setStatus('idle'); setError(null); } };
}

export default useAutosave;
